import type { Storey } from '@manudev/assistant';
import type { SectionOptions } from '@manudev/model';
import type * as THREE from 'three';
import { useHeroStore } from '@/store/store';
import { useDrawingsStore, type SectionAxis } from './state';

export const PLAN_CUT = 1.2;
const DEFAULT_STOREY = 'Level 1';
// keeps the slider's ends inside the outer walls
const INSET = 0.3;

// In the model frame, facing away from the camera: the half a section looks at is the half
// that stays standing in 3D.
export const SECTION_NORMALS: Record<SectionAxis, [number, number, number]> = {
  x: [-1, 0, 0],
  z: [0, 0, 1],
};

let inFlight = false;
let drawnFrom: unknown = null;

export function sectionAt(box: THREE.Box3, axis: SectionAxis, at: number): SectionOptions {
  const k = axis === 'x' ? 0 : 2;
  const lo = box.min.getComponent(k) + INSET;
  const hi = box.max.getComponent(k) - INSET;
  const origin = box.getCenter(box.min.clone()).toArray();
  origin[k] = lo + (hi - lo) * at;
  return { origin, normal: SECTION_NORMALS[axis] };
}

export function sortedStoreys(storeys: Storey[]): Storey[] {
  return [...storeys].sort((a, b) => (a.elevation ?? 0) - (b.elevation ?? 0));
}

// One request at a time, always for the latest ask: whatever changed while one was out is
// picked up on the next frame after it lands. An edit to the model redraws the same view.
export function syncDrawing(box: THREE.Box3): void {
  const store = useHeroStore.getState();
  if (store.status !== 'ready' || inFlight) return;
  const s = useDrawingsStore.getState();
  const storey = store.storeys.find((x) => x.expressID === s.storeyId);
  if (!storey) {
    const first = defaultStorey(store.storeys);
    if (first) s.setStorey(first.expressID);
    return;
  }
  const key =
    s.mode === 'plan' ? `plan:${storey.expressID}` : `section:${s.axis}:${s.at.toFixed(2)}`;
  if (key === s.drawn && store.elements === drawnFrom) return;

  const title = s.mode === 'plan' ? `${storey.name} plan` : `Section ${s.axis.toUpperCase()}`;
  const elements = store.elements;
  inFlight = true;
  useDrawingsStore.setState({ busy: true });
  const job =
    s.mode === 'plan'
      ? store.plan({ storeyId: storey.expressID, elevationOffset: PLAN_CUT })
      : store.section(sectionAt(box, s.axis, s.at));
  job
    .then(
      (drawing) => useDrawingsStore.setState({ drawing, drawn: key, title, error: null }),
      (err: Error) => useDrawingsStore.setState({ drawn: key, title, error: err.message }),
    )
    .finally(() => {
      inFlight = false;
      drawnFrom = elements;
      useDrawingsStore.setState({ busy: false });
    });
}

function defaultStorey(storeys: Storey[]): Storey | undefined {
  const sorted = sortedStoreys(storeys);
  return (
    sorted.find((s) => s.name === DEFAULT_STOREY) ??
    sorted.find((s) => (s.elevation ?? 0) >= 0) ??
    sorted[0]
  );
}
